import { msrpWsUrl } from "@/config/api";
import EventEmitter from "events";
import { ReconnectionManager } from "../utils/reconnectionManager";
//const WebSocket = require('ws');

class ConnectionManager extends EventEmitter {
  static connections = new Map();
  static activeInstance = null;
  static lifecycleBound = false;
  constructor(endpoint) {
    console.log("constructing connection manager");
    super();
    this.packetParser = null;
    this.endpoint = endpoint;
    this.connections = ConnectionManager.connections;

    // Reconnection support
    this.reconnectionManager = new ReconnectionManager();
    this.connectionState = 'disconnected';
    this.currentProtocol = null;
    this.currentHost = null;
    this.currentPort = null;
    this.isManualDisconnect = false;
    this.isSessionClosed = false;
    this.isTimedOut = false;
    this.debounceTimer = null;
    this.reconnectionTimeoutTimer = null;
    this.lastRxTime = 0;
    this.healthCheckTimer = null;

    ConnectionManager.activeInstance = this;
    ConnectionManager.ensureLifecycleListeners();
  }

  static ensureLifecycleListeners() {
    if (ConnectionManager.lifecycleBound) return;
    if (typeof window === "undefined" || typeof document === "undefined") return;
    ConnectionManager.lifecycleBound = true;

    document.addEventListener("visibilitychange", () => {
      const mgr = ConnectionManager.activeInstance;
      if (!mgr) return;
      if (document.visibilityState === "visible") {
        mgr.handleResume("visibilitychange");
      } else {
        mgr.handleHidden();
      }
    });

    window.addEventListener("pageshow", (event) => {
      if (event.persisted) {
        ConnectionManager.activeInstance?.handleResume("pageshow");
      }
    });

    window.addEventListener("online", () => {
      ConnectionManager.activeInstance?.handleResume("online");
    });
  }

  handleHidden() {
    console.log("[Lifecycle] Page hidden - pausing reconnection budget");
    this.reconnectionManager.pause();

    if (this.reconnectionTimeoutTimer) {
      clearTimeout(this.reconnectionTimeoutTimer);
      this.reconnectionTimeoutTimer = null;
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.clearHealthCheck();
  }

  handleResume(source) {
    console.log(`[Lifecycle] Page resumed (${source})`);

    if (this.isSessionClosed) {
      console.log("[Lifecycle] Session was closed intentionally - not resuming");
      return;
    }

    this.reconnectionManager.resume();

    const socket = this.connections.get("ws");
    const isOpen = socket && socket.readyState === WebSocket.OPEN;

    if (!isOpen) {
      console.log("[Lifecycle] Socket not open on resume - reconnecting immediately");
      this.resumeConnection();
      return;
    }

    this.startHealthCheck();
  }

  resumeConnection() {
    this.isTimedOut = false;
    this.isManualDisconnect = false;
    this.reconnectionManager.reset();

    if (this.reconnectionTimeoutTimer) {
      clearTimeout(this.reconnectionTimeoutTimer);
      this.reconnectionTimeoutTimer = null;
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    if (!this.currentProtocol) {
      console.log("[Lifecycle] No previous connection details - nothing to resume");
      return;
    }

    this.connectionState = 'reconnecting';
    this.emit("connectionStateChanged", 'reconnecting');
    this.connections.delete(this.currentProtocol);
    this.openSocket(this.currentProtocol, this.currentHost, this.currentPort, true);
  }

  startHealthCheck(deadlineMs = 5000) {
    this.clearHealthCheck();

    const rxBefore = this.lastRxTime;
    console.log("[Lifecycle] Socket looks open - verifying with a ping");
    this.emit("healthCheckRequested");

    this.healthCheckTimer = setTimeout(() => {
      this.healthCheckTimer = null;
      if (this.lastRxTime > rxBefore) {
        console.log("[Lifecycle] Socket verified alive");

        if (this.connectionState === 'offline') {
          this.connectionState = 'authenticated';
          this.emit("connectionStateChanged", 'authenticated');
        }
        return;
      }
      console.warn("[Lifecycle] No response to health-check ping - socket is half-open, closing");
      const socket = this.connections.get("ws");
      if (socket) {
        socket.close();
      } else {
        this.resumeConnection();
      }
    }, deadlineMs);
  }

  clearHealthCheck() {
    if (this.healthCheckTimer) {
      clearTimeout(this.healthCheckTimer);
      this.healthCheckTimer = null;
    }
  }

  setPacketParser(parser) {
    this.packetParser = parser;

    //emit job is to trigger named events
    if (this.packetParser) {
      this.packetParser.on("protocolSwitch", (message) => {
        this.emit("protocolSwitch", message);
      });

      this.packetParser.on("403", (rawMessage) => {
        this.emit("403", rawMessage);
      });

      this.packetParser.on("401", (rawMessage) => {
        this.emit("401", rawMessage);
      });

      this.packetParser.on("auth_200", (rawMessage) => {
        this.emit("auth_200", rawMessage);
      });

      this.packetParser.on("SEND", (message) => {
        this.emit("SEND", message);
      });
    }
  }

  openSocket(protocol, host, port, isReconnecting = false) {
    console.log("open socket", protocol, host, port, "isReconnecting:", isReconnecting);

    if (this.isSessionClosed) {
      console.log("Session closed - not opening a new socket");
      return;
    }

    this.isManualDisconnect = false;

    // Store connection details for reconnection
    this.currentProtocol = protocol;
    this.currentHost = host;
    this.currentPort = port;

    if (protocol === "ws") {
      console.log("connectionswew", this.connections);

      if (this.connections.size > 0) {
        this.endpoint.emit("socketOpened", protocol);
        return;
      }

      // Update connection state ONLY if not reconnecting
      // If reconnecting, keep the 'reconnecting' state
      if (!isReconnecting) {
        this.connectionState = 'connecting';
        this.emit("connectionStateChanged", 'connecting');
      }

      //native WebSocket API provided by the browser
      // const socket = new WebSocket(`ws://${host}:${port}`, "msrp");
      // const socket = new WebSocket(`ws://127.0.0.1:3457`, "msrp");



      const socket = new WebSocket(msrpWsUrl, "msrp");
      socket.onopen = () => {
        console.log(`Connected to ${host}:${port} using WebSocket`);
        this.lastRxTime = Date.now();
        this.isTimedOut = false;
        ConnectionManager.activeInstance = this;
        console.log("connections after set", this.connections);
        this.connections.set(protocol, socket);

        console.log("connections array", this.connections);
        console.log("after", this.connections.size);

        // Update connection state
        this.connectionState = 'connected';
        this.emit("connectionStateChanged", 'connected');

        // Reset reconnection manager on successful connection
        this.reconnectionManager.reset();

        if (this.reconnectionTimeoutTimer) {
          clearTimeout(this.reconnectionTimeoutTimer);
          this.reconnectionTimeoutTimer = null;
        }

        this.endpoint.emit("socketOpened", protocol);
      };

      socket.onmessage = (event) => {
        this.lastRxTime = Date.now();
        let data = event.data;

        // Check if the data is a Blob
        if (data instanceof Blob) {
          data
            .text()
            .then((text) => {
              console.log("Received WebSocket data as Blob, converted to text:", text);
              try {
                this.packetParser.parse(text);
              } catch (err) {
                console.error("Failed to parse MSRP message:", err);
              }
            })
            .catch((err) => {
              console.error("Error reading Blob data:", err);
            });
        } else {
          console.log("Received WebSocket data:", data);
          try {
            // Pass the data to the message parser directly if it's already a string
            this.packetParser.parse(data);
          } catch (err) {
            console.error("Failed to parse MSRP message:", err);
          }
        }
      };

      socket.onerror = (err) => {
        console.error(`WebSocket connection error:`, err);
        this.emit("error", new Error(`WebSocket connection error unexpectedly`));

        // Don't trigger reconnection here - let onclose handle it
      };

      socket.onclose = (event) => {
        console.log(`WebSocket closed: code=${event.code}, reason=${event.reason}, clean=${event.wasClean}`);

        this.clearHealthCheck();

        // Emit connection closed event
        this.emit("connectionClosed", {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean
        });

        // Check if this was a manual disconnect
        if (this.isManualDisconnect) {
          console.log("Manual disconnect - not reconnecting");
          this.connectionState = 'disconnected';
          this.emit("connectionStateChanged", 'disconnected');
          this.connections.delete(protocol);
          return;
        }

        // Start reconnection with debouncing
        this.startReconnection(protocol);
      };
    } else {
      console.error("Unsupported protocol:", protocol);
    }
  }

  // TODO: delete this after tests
  // openConnection(protocol, host, port) {
  //   if (protocol === "ws") {
  //     console.log("connectionsarr", this.connections);
  //     console.log("before", this.connections.length);

  //     if (this.connections.length > 0) {
  //       this.emit("connected", protocol);
  //       return;
  //     }

  //     //native WebSocket API provided by the browser
  //     const socket = new WebSocket("ws://127.0.0.1:3457", "msrp");
  //     socket.onopen = () => {
  //       console.log(`Connected to ${host}:${port} using WebSocket`);
  //       this.connections.set(protocol, socket);

  //       console.log("connections after set", this.connections);

  //       this.emit("connected", protocol);
  //     };

  //     socket.onmessage = (event) => {
  //       let data = event.data;

  //       // Check if the data is a Blob
  //       if (data instanceof Blob) {
  //         data
  //           .text()
  //           .then((text) => {
  //             console.log("Received WebSocket data as Blob, converted to text:", text);
  //             try {
  //               this.packetParser.parse(text);
  //             } catch (err) {
  //               console.error("Failed to parse MSRP message:", err);
  //             }
  //           })
  //           .catch((err) => {
  //             console.error("Error reading Blob data:", err);
  //           });
  //       } else {
  //         console.log("Received WebSocket data:", data);
  //         try {
  //           // Pass the data to the message parser directly if it's already a string
  //           this.packetParser.parse(data);
  //         } catch (err) {
  //           console.error("Failed to parse MSRP message:", err);
  //         }
  //       }
  //     };

  //     socket.onerror = (err) => {
  //       console.error(`WebSocket connection error:`, err);
  //     };

  //     socket.onclose = () => {
  //       console.log(`WebSocket connection closed`);
  //       this.connections.delete(protocol);
  //     };
  //   } else {
  //     console.error("Unsupported protocol:", protocol);
  //   }
  // }

  sendMessage(protocol, message) {
    console.log("connectionManager :: send message function is called");
    const socket = this.connections.get(protocol);
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(message);
      console.log("message sent successfully");
    } else {
      console.warn(`No active connection found for protocol ${protocol}`);
    }
  }

  closeConnection(protocol, isSessionClose = false) {
    const socket = this.connections.get(protocol);
    if (socket) {
      // Mark as manual disconnect to prevent reconnection
      this.isManualDisconnect = true;

      if (isSessionClose) {
        this.isSessionClosed = true;
        console.log(`${protocol} connection closed due to session-close - reconnection permanently disabled`);
      }

      socket.close();
      console.log(`${protocol} connection closed manually`);
      this.connections.delete(protocol);
      this.clearHealthCheck();

      // Clear reconnection manager
      this.reconnectionManager.clearTimer();
    } else {
      console.warn(`No active connection found for protocol ${protocol}`);
    }
  }

  resetSessionClosed() {
    this.isSessionClosed = false;
    this.isTimedOut = false;
    console.log("Session closed flag reset - reconnection enabled");
  }

  /**
   * Start reconnection with exponential backoff
   */
  startReconnection(protocol) {
    if (this.isSessionClosed) {
      console.log("Session closed - not attempting reconnection");
      return;
    }

    if (this.isTimedOut) {
      console.log("Reconnection budget exhausted - waiting for resume or manual retry");
      return;
    }

    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      console.log("[Lifecycle] Page hidden - deferring reconnection until visible");
      this.reconnectionManager.pause();
      return;
    }

    if (!this.reconnectionTimeoutTimer) {
      const timeoutMs = this.reconnectionManager.config.maxTotalTime;
      console.log(`Hard reconnection timeout started: ${timeoutMs / 1000}s`);
      this.reconnectionTimeoutTimer = setTimeout(() => {
        console.log("Hard reconnection timeout reached - stopping reconnection");
        this.forceTimeout(protocol);
      }, timeoutMs);
    }

    // Clear any existing debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Debounce rapid reconnection attempts (500ms)
    this.debounceTimer = setTimeout(() => {
      this.attemptReconnection(protocol);
    }, 500);
  }

  forceTimeout(protocol) {
    if (this.reconnectionTimeoutTimer) {
      clearTimeout(this.reconnectionTimeoutTimer);
      this.reconnectionTimeoutTimer = null;
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.reconnectionManager.clearTimer();

    this.connectionState = 'offline';
    this.isTimedOut = true;
    this.emit("connectionStateChanged", 'offline');
    this.emit("maxRetriesReached");
    this.connections.delete(protocol);
  }

  /**
   * Attempt reconnection
   */
  attemptReconnection(protocol) {
    if (this.isSessionClosed || this.isTimedOut) {
      console.log("Session closed or timed out - aborting reconnection attempt");
      return;
    }

    if (this.reconnectionManager.shouldStopReconnecting()) {
      const reason = this.reconnectionManager.isTimeoutReached()
        ? "Reconnection timeout reached"
        : "Max reconnection attempts reached";
      console.error(reason);
      this.connectionState = 'offline';
      this.isTimedOut = true;
      this.reconnectionManager.clearTimer();
      this.emit("connectionStateChanged", 'offline');
      this.emit("maxRetriesReached");
      this.connections.delete(protocol);
      return;
    }

    // Update state
    this.connectionState = 'reconnecting';
    this.emit("connectionStateChanged", 'reconnecting');

    // Schedule reconnection with exponential backoff
    this.reconnectionManager.scheduleReconnect(() => {
      console.log(`Reconnection attempt ${this.reconnectionManager.getAttemptCount()}/${this.reconnectionManager.getMaxRetries()}`);

      // Emit reconnecting event with details
      this.emit("reconnecting", {
        attempt: this.reconnectionManager.getAttemptCount(),
        maxRetries: this.reconnectionManager.getMaxRetries(),
        nextRetryIn: 0 // Already retrying
      });

      // Remove old socket from connections
      this.connections.delete(protocol);

      // Attempt to reconnect using stored connection details
      // Pass isReconnecting = true to keep 'reconnecting' state
      this.openSocket(
        this.currentProtocol,
        this.currentHost,
        this.currentPort,
        true // isReconnecting
      );
    });
  }

  /**
   * Manual reconnect (reset retry counter)
   */
  manualReconnect() {
    console.log("Manual reconnect triggered");
    this.resumeConnection();
  }

  /**
   * Get current connection state
   */
  getConnectionState() {
    return this.connectionState;
  }
}

export default ConnectionManager;
